/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef } from "react";
import {
  Modal,
  BlockStack,
  Text,
  RadioButton,
  TextField,
  DropZone,
  InlineStack,
  Button,
  Box,
  Spinner,
  Link,
  Checkbox,
} from "@medentic/polaris-react";
import { XIcon } from "@medentic/polaris-icons";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import useAIContext, { AIContextEntityType } from "@shared/hooks/events/useAIContext";
import GenerativeTextField from "../GenerativeTextField/GenerativeTextField";
import { useTranslation } from "react-i18next";
import { Event, Instructor, Venue, Brand, ImageDetailType, MediaItem, MultilingualText } from "@features/events/types";
import { showToast as showToastAction } from "@shared/lib";

const AUTOMATIC_IMAGE_DESCRIPTION_COOKIE = "automatically_generate_image_descriptions";
const AUTOMATIC_IMAGE_DESCRIPTION_COOKIE_EXPIRATION_DAYS = 400;

interface UploadResponseImage {
  src: string;
  width: number;
  height: number;
  type: string;
  size: number;
}

interface AddMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMedia: (media: MediaItem[]) => void;
  onEditMedia?: (media: MediaItem) => void;
  editingItem?: MediaItem | null;
  type?: "vid" | "img";
  hcc?: string;
  entityType?: MediaEntityType;
  entityId?: string;
  // Entity data for enhanced AI context
  eventData?: Partial<Event>;
  // For events: pass instructors and venues to enrich context
  eventInstructors?: Partial<Instructor>[];
  eventVenues?: Partial<Venue>[];
  // Single entity data for non-event entities
  instructorData?: Partial<Instructor>;
  venueData?: Partial<Venue>;
  brandData?: Partial<Brand>;
}

export enum MediaEntityType {
  EVENT = "event",
  INSTRUCTOR = "instructor",
  VENUE = "venue",
  BRAND = "brand",
}

interface PendingImage {
  id: string;
  file: File;
  previewUrl: string;
  name: MultilingualText;
  error: string;
  isLoadingDescription?: boolean;
}

// VideoForm Component - handles single video upload
interface VideoFormProps {
  mediaName: MultilingualText;
  youtubeUrl: string;
  errors: { name: string; youtubeUrl: string };
  onNameChange: (value: MultilingualText) => void;
  onUrlChange: (value: string) => void;
}

function VideoForm({
  mediaName,
  youtubeUrl,
  errors,
  onNameChange,
  onUrlChange,
}: VideoFormProps) {
  const { t } = useTranslation();

  return (
    <BlockStack gap="300">
      <BlockStack gap="200">
        <GenerativeTextField
          id="video-title-bg"
          placementId="event.event.video.title"
          label={t("addMediaModal.videoTitle")}
          value={mediaName}
          onChange={onNameChange}
          type="textField"
          canGenerate={true}
          requiredIndicator
        />
      </BlockStack>

      <BlockStack gap="200">
        <Text as="p" variant="bodyMd" requiredIndicator>
          {t("addMediaModal.youtubeLink")}
        </Text>
        <TextField
          label=""
          value={youtubeUrl}
          onChange={onUrlChange}
          placeholder={t("addMediaModal.youtubeLinkPlaceholder")}
          error={errors.youtubeUrl}
          autoComplete="off"
        />
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("addMediaModal.youtubeOnlySupported")}
        </Text>
      </BlockStack>
    </BlockStack>
  );
}

// ImagesForm Component - handles multiple image uploads
interface ImagesFormProps {
  pendingImages: PendingImage[];
  onAddFiles: (files: File[]) => void;
  onRemoveImage: (id: string) => void;
  onUpdateImageName: (id: string, name: MultilingualText) => void;
  onCancelDescriptionLoading: (id: string) => void;
}

function ImagesForm({
  pendingImages,
  onAddFiles,
  onRemoveImage,
  onUpdateImageName,
  onCancelDescriptionLoading,
}: ImagesFormProps) {
  const { t } = useTranslation();

  return (
    <BlockStack gap="400">
      {/* DropZone always visible at top */}
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {t("addMediaModal.uploadImages")}
        </Text>
        <DropZone
          onDrop={onAddFiles}
          accept="image/png,image/jpg, image/jpeg, image/gif, image/webp, image/avif, image/heic, image/tiff, image/svg"
          allowMultiple
        >
          <DropZone.FileUpload
            actionTitle={t("addMediaModal.addImageOrPhoto")}
          />
        </DropZone>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("addMediaModal.imageResizeNote")}
        </Text>
      </BlockStack>

      {/* List of pending images */}
      {pendingImages.length > 0 && (
        <BlockStack gap="300">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {t("addMediaModal.pendingImages")} ({pendingImages.length})
          </Text>
          {pendingImages.map((image) => (
            <Box
              key={image.id}
              padding="400"
              borderWidth="025"
              borderColor="border"
              borderRadius="200"
              background="bg-surface"
            >
              <BlockStack gap="300">
                <InlineStack gap="300" align="space-between">
                  <InlineStack gap="300" blockAlign="center">
                    <img
                      src={image.previewUrl}
                      alt={t("addMediaModal.previewAlt")}
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #e1e1e1",
                      }}
                    />
                    <Text as="p" variant="bodyMd">
                      {image.file.name}
                    </Text>
                  </InlineStack>
                  <Button
                    icon={XIcon}
                    variant="plain"
                    tone="critical"
                    onClick={() => onRemoveImage(image.id)}
                    accessibilityLabel={t("addMediaModal.removeImageLabel")}
                  />
                </InlineStack>

                {image.isLoadingDescription ? (
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      {t("addMediaModal.imageTitle")}
                    </Text>
                    <InlineStack gap="200" blockAlign="center">
                      <Spinner size="small" />
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t("addMediaModal.description_loading")}
                      </Text>
                      <Text as="span" variant="bodySm">
                        —
                      </Text>
                      <Link onClick={() => onCancelDescriptionLoading(image.id)}>
                        {t("addMediaModal.writeItManually")}
                      </Link>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <GenerativeTextField
                    id={`image-title-${image.id}`}
                    placementId="event.event.image.title"
                    label={t("addMediaModal.imageTitle")}
                    value={image.name}
                    onChange={(value) => onUpdateImageName(image.id, value)}
                    type="textField"
                    canGenerate={true}
                    requiredIndicator
                    error={image.error}
                  />
                )}
              </BlockStack>
            </Box>
          ))}
        </BlockStack>
      )}
    </BlockStack>
  );
}

export default function AddMediaModal({
  isOpen,
  onClose,
  onAddMedia,
  onEditMedia,
  editingItem,
  type,
  hcc,
  entityType = MediaEntityType.EVENT,
  entityId,
  eventData,
  eventInstructors,
  eventVenues,
  instructorData,
  venueData,
  brandData,
}: AddMediaModalProps) {
  const { t } = useTranslation();
  const { POST } = useApiRequest();
  const dispatch = useDispatch();
  const { buildContext } = useAIContext();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );
  
  // Track cancelled image IDs to ignore their results when they complete
  const cancelledImageIdsRef = useRef<Set<string>>(new Set());
  
  // AbortController refs for cancellable requests (for cleanup)
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const descriptionBatchVersionRef = useRef(0);

  const [mediaType, setMediaType] = useState<"vid" | "img">(
    type || editingItem?.type || "vid"
  );

  // Video state
  const [mediaName, setMediaName] = useState<MultilingualText>({});
  const [youtubeUrl, setYoutubeUrl] = useState(
    editingItem?.type === "vid" ? editingItem.source || "" : ""
  );
  const [errors, setErrors] = useState<{
    name: string;
    youtubeUrl: string;
  }>({
    name: "",
    youtubeUrl: "",
  });

  // Image state
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  // Edit mode state for single image
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(() => {
    if (editingItem?.type === "img") {
      if (Array.isArray(editingItem.source)) {
        return editingItem.source[0]?.src || null;
      } else if (typeof editingItem.source === "string") {
        return editingItem.source;
      }
    }
    return null;
  });
  const [automaticallyGenerateDescription, setAutomaticallyGenerateDescription] = useState(
    Cookies.get(AUTOMATIC_IMAGE_DESCRIPTION_COOKIE) !== "false"
  );

  useEffect(() => {
    if (isOpen) {
      const storedAutomaticDescriptionPreference =
        Cookies.get(AUTOMATIC_IMAGE_DESCRIPTION_COOKIE) !== "false";
      setAutomaticallyGenerateDescription(storedAutomaticDescriptionPreference);
      Cookies.set(
        AUTOMATIC_IMAGE_DESCRIPTION_COOKIE,
        String(storedAutomaticDescriptionPreference),
        {
          expires: AUTOMATIC_IMAGE_DESCRIPTION_COOKIE_EXPIRATION_DAYS,
          path: "/",
          sameSite: "lax",
        }
      );

      if (editingItem) {
        // Edit mode
        setMediaType(editingItem.type);
        setMediaName(editingItem.name);
        setYoutubeUrl(
          editingItem.type === "vid" ? editingItem.source || "" : ""
        );

        let imagePreviewUrl = null;
        if (editingItem.type === "img") {
          if (Array.isArray(editingItem.source)) {
            imagePreviewUrl = editingItem.source[0]?.src || null;
          } else if (typeof editingItem.source === "string") {
            imagePreviewUrl = editingItem.source;
          }
        }

        setPreviewUrl(imagePreviewUrl);
        setUploadedFile(null);
        setPendingImages([]);
      } else {
        // Add mode
        setMediaName({});
        setYoutubeUrl("");
        setUploadedFile(null);
        setPreviewUrl(null);
        setPendingImages([]);
      }
      setErrors({ name: "", youtubeUrl: "" });
      setUploadProgress(null);
    }
  }, [isOpen, editingItem, type]);

  useEffect(() => {
    return () => {
      // Cleanup preview URLs
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      pendingImages.forEach((img) => {
        URL.revokeObjectURL(img.previewUrl);
      });
    };
  }, [previewUrl, pendingImages]);

  // Map MediaEntityType to AIContextEntityType
  const getAIContextEntityType = (type: MediaEntityType): AIContextEntityType => {
    switch (type) {
      case MediaEntityType.EVENT:
        return AIContextEntityType.EVENT;
      case MediaEntityType.INSTRUCTOR:
        return AIContextEntityType.INSTRUCTOR;
      case MediaEntityType.VENUE:
        return AIContextEntityType.VENUE;
      case MediaEntityType.BRAND:
        return AIContextEntityType.BRAND;
      default:
        return AIContextEntityType.EVENT;
    }
  };

  // Build context for AI image description using the hook
  const getContextForAI = (): string => {
    const aiEntityType = getAIContextEntityType(entityType);
    return buildContext(aiEntityType, {
      event: eventData,
      instructors: eventInstructors,
      venues: eventVenues,
      instructor: instructorData,
      venue: venueData,
      brand: brandData,
    });
  };

  // Helper function to resize image and convert to base64
  const resizeImageToBase64 = (file: File, maxSize: number = 500): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with JPEG format for smaller size
          const base64 = canvas.toDataURL("image/jpeg", 0.8);
          resolve(base64);
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  // Function to get image description from API
  const getImageDescription = async (file: File, abortSignal?: AbortSignal): Promise<MultilingualText> => {
    try {
      const imageBase64 = await resizeImageToBase64(file);
      const context = getContextForAI();

      // Check if aborted before making request
      if (abortSignal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }

      const response = await POST<{ imageDescription: MultilingualText }>(
        "/api/languages/describe-image",
        {
          imageBase64,
          context,
          toLanguages: selectedLanguages,
        },
        false, // addUTMParams
        false, // logoutOnFail
        true, // suppressToastErrors - we'll handle errors manually
        abortSignal
      );

      // Check if aborted after request completed
      if (abortSignal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }

      // Return the multilingual description directly
      return response.imageDescription || {};
    } catch (error) {
      // Re-throw cancellation errors without logging
      if (
        (error instanceof DOMException && error.name === "AbortError") ||
        (typeof error === "object" &&
          error !== null &&
          "code" in error &&
          error.code === "ERR_CANCELED")
      ) {
        throw error;
      }
      console.error("Image description failed:", error);
      throw error;
    }
  };

  // Handler for adding multiple files (new multi-image flow)
  const handleAddFiles = async (files: File[]) => {
    const descriptionBatchVersion = descriptionBatchVersionRef.current;
    const newImages: PendingImage[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: {},
      error: "",
      isLoadingDescription: automaticallyGenerateDescription,
    }));
    setPendingImages((prev) => [...prev, ...newImages]);

    if (!automaticallyGenerateDescription) {
      return;
    }

    // Get descriptions for each image
    for (const image of newImages) {
      if (descriptionBatchVersionRef.current !== descriptionBatchVersion) {
        return;
      }

      if (cancelledImageIdsRef.current.has(image.id)) {
        cancelledImageIdsRef.current.delete(image.id);
        continue;
      }

      // Create AbortController for this image
      const abortController = new AbortController();
      abortControllersRef.current.set(image.id, abortController);

      try {
        const description = await getImageDescription(image.file, abortController.signal);
        
        // Check if this image was cancelled while we were waiting for the API
        // If cancelled, don't update the description - user wants to write manually
        if (cancelledImageIdsRef.current.has(image.id)) {
          cancelledImageIdsRef.current.delete(image.id);
          // Don't update - loading was already set to false by cancel handler
        } else {
          setPendingImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? { ...img, name: description, isLoadingDescription: false }
                : img
            )
          );
        }
      } catch (error) {
        // If aborted or cancelled, just stop loading without error toast
        if (
          (error instanceof DOMException && error.name === "AbortError") ||
          (typeof error === "object" &&
            error !== null &&
            "code" in error &&
            error.code === "ERR_CANCELED")
        ) {
          // Already handled by cancel handler
          cancelledImageIdsRef.current.delete(image.id);
        } else if (cancelledImageIdsRef.current.has(image.id)) {
          // Was cancelled but error happened - just cleanup
          cancelledImageIdsRef.current.delete(image.id);
        } else {
          console.error(`Failed to get description for ${image.file.name}:`, error);
          dispatch(
            showToastAction({
              message: t("addMediaModal.description_loading_failed", {
                fileName: image.file.name,
              }),
              type: "error",
            })
          );
          setPendingImages((prev) =>
            prev.map((img) =>
              img.id === image.id ? { ...img, isLoadingDescription: false } : img
            )
          );
        }
      } finally {
        // Clean up AbortController
        abortControllersRef.current.delete(image.id);
      }
    }
  };

  // Handler for cancelling description loading (user wants to write manually)
  const handleCancelDescriptionLoading = (imageId: string) => {
    // Mark this image as cancelled so we ignore the result when it comes back
    cancelledImageIdsRef.current.add(imageId);
    
    // Abort the active request
    const abortController = abortControllersRef.current.get(imageId);
    if (abortController) {
      abortController.abort();
    }
    
    // Immediately set loading to false so user can type
    setPendingImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, isLoadingDescription: false } : img
      )
    );
  };

  const handleAutomaticGenerationChange = (enabled: boolean) => {
    console.log("[AddMediaModal::handleAutomaticGenerationChange]");
    setAutomaticallyGenerateDescription(enabled);
    Cookies.set(AUTOMATIC_IMAGE_DESCRIPTION_COOKIE, String(enabled), {
      expires: AUTOMATIC_IMAGE_DESCRIPTION_COOKIE_EXPIRATION_DAYS,
      path: "/",
      sameSite: "lax",
    });

    if (enabled) {
      return;
    }

    descriptionBatchVersionRef.current += 1;
    pendingImages.forEach((image) => {
      if (image.isLoadingDescription) {
        cancelledImageIdsRef.current.add(image.id);
      }
    });
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });

    setPendingImages((prev) =>
      prev.map((image) => ({ ...image, isLoadingDescription: false }))
    );
  };

  // Handler for removing an image from pending list
  const handleRemoveImage = (id: string) => {
    setPendingImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter((img) => img.id !== id);
    });
  };

  // Handler for updating image name
  const handleUpdateImageName = (id: string, name: MultilingualText) => {
    setPendingImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, name, error: "" } : img
      )
    );
  };

  // Validation for video
  const validateVideoForm = () => {
    const newErrors = {
      name: "",
      youtubeUrl: "",
    };

    if (!Object.values(mediaName).some((value) => value.trim())) {
      newErrors.name = t("addMediaModal.validation.titleRequired");
    }

    if (!(youtubeUrl as string)?.trim()) {
      newErrors.youtubeUrl = t("addMediaModal.validation.youtubeUrlRequired");
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  // Validation for images
  const validateImagesForm = () => {
    let isValid = true;
    const updatedImages = pendingImages.map((img) => {
      const hasTitle = Object.values(img.name).some((value) => value?.trim());
      if (!hasTitle) {
        isValid = false;
        return { ...img, error: t("addMediaModal.validation.titleRequired") };
      }
      return { ...img, error: "" };
    });

    setPendingImages(updatedImages);
    return isValid;
  };

  // Validation for edit mode (single image)
  const validateEditImageForm = () => {
    const newErrors = {
      name: "",
      youtubeUrl: "",
    };

    if (!Object.values(mediaName).some((value) => value.trim())) {
      newErrors.name = t("addMediaModal.validation.titleRequired");
    }

    if (!uploadedFile && !previewUrl) {
      dispatch(showToastAction({ message: t("addMediaModal.validation.imageFileRequired"), type: "error" }));
      return false;
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  // Upload a single image file
  const uploadSingleImage = async (file: File): Promise<UploadResponseImage[]> => {
    const formData = new FormData();
    formData.append("file", file);

    if (!hcc || !entityId) {
      throw new Error("HCC and Entity ID are required for media upload");
    }

    let endpoint: string;
    switch (entityType) {
      case MediaEntityType.INSTRUCTOR:
        endpoint = `/api/healthcare-companies/${hcc}/e/instructors/${entityId}/media`;
        break;
      case MediaEntityType.VENUE:
        endpoint = `/api/healthcare-companies/${hcc}/e/venues/${entityId}/media`;
        break;
      case MediaEntityType.BRAND:
        endpoint = `/api/healthcare-companies/${hcc}/e/brands/${entityId}/media`;
        break;
      case MediaEntityType.EVENT:
      default:
        endpoint = `/api/healthcare-companies/${hcc}/e/events/${entityId}/media`;
        break;
    }

    return POST(endpoint, formData) as Promise<UploadResponseImage[]>;
  };

  const handleSubmit = async () => {
    // Case 1: Video submission
    if (mediaType === "vid") {
      if (!validateVideoForm()) {
        return;
      }

      const mediaItem: MediaItem = {
        type: "vid",
        name: mediaName,
        source: youtubeUrl as string,
      };

      onAddMedia([mediaItem]);
      handleClose();
      return;
    }

    // Case 2: Edit mode for single image
    if (editingItem && mediaType === "img") {
      if (!validateEditImageForm()) {
        return;
      }

      let finalUrl = youtubeUrl;

      if (uploadedFile) {
        try {
          setUploadProgress({ current: 1, total: 1 });
          const response = await uploadSingleImage(uploadedFile);
          finalUrl = response;
          if (!finalUrl) {
            throw new Error("No image URL received from upload");
          }
        } catch (error) {
          console.error("Image upload failed:", error);
          dispatch(showToastAction({ message: t("addMediaModal.uploadFailed"), type: "error" }));
          setUploadProgress(null);
          return;
        }
      } else if (previewUrl === null) {
        finalUrl = "";
      } else if (editingItem.type === "img" && previewUrl) {
        finalUrl = editingItem.source;
      } else {
        finalUrl = previewUrl || "";
      }

      const mediaItem: MediaItem = {
        type: "img",
        name: mediaName,
        source: finalUrl as ImageDetailType[],
      };

      if (onEditMedia) {
        onEditMedia(mediaItem);
      }

      setUploadProgress(null);
      handleClose();
      return;
    }

    // Case 3: Multi-image upload (new flow)
    if (mediaType === "img" && !editingItem) {
      if (pendingImages.length === 0) {
        dispatch(showToastAction({ message: t("addMediaModal.validation.noImagesSelected"), type: "error" }));
        return;
      }

      if (!validateImagesForm()) {
        return;
      }

      // Upload all images
      const uploadedMediaItems: MediaItem[] = [];
      const failedImages: string[] = [];

      setUploadProgress({ current: 0, total: pendingImages.length });

      for (let i = 0; i < pendingImages.length; i++) {
        const image = pendingImages[i];
        setUploadProgress({ current: i + 1, total: pendingImages.length });

        try {
          const response = await uploadSingleImage(image.file);
          uploadedMediaItems.push({
            type: "img",
            name: image.name,
            source: response,
          });
        } catch (error) {
          console.error(`Image upload failed for ${image.file.name}:`, error);
          failedImages.push(image.file.name);
          // Remove failed image from pending list
          setPendingImages((prev) => prev.filter((img) => img.id !== image.id));
        }
      }

      // Show error toast if any images failed
      if (failedImages.length > 0) {
        dispatch(showToastAction({
          message: `${t("addMediaModal.uploadFailed")}: ${failedImages.join(", ")}`,
          type: "error"
        }));
      }

      // Pass uploaded items to parent (even if some failed)
      if (uploadedMediaItems.length > 0) {
        onAddMedia(uploadedMediaItems);
        handleClose();
      } else {
        // All uploads failed
        setUploadProgress(null);
      }
    }
  };

  const handleClose = () => {
    descriptionBatchVersionRef.current += 1;

    // Abort any pending description requests
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
    
    // Clear cancelled tracking refs
    cancelledImageIdsRef.current.clear();

    setMediaName({});
    setYoutubeUrl("");
    setUploadedFile(null);
    
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    
    // Cleanup pending images
    pendingImages.forEach((img) => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setPendingImages([]);
    
    setErrors({ name: "", youtubeUrl: "" });
    setUploadProgress(null);
    onClose();
  };

  // Edit mode: Single file upload handler
  const handleFileUpload = (files: File[]) => {
    if (files.length > 0) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const file = files[0];
      setUploadedFile(file);

      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setUploadedFile(null);
    setPreviewUrl(null);
  };

  // Edit mode: File upload preview component
  const fileUpload = (uploadedFile || previewUrl) && (
    <div
      style={{
        padding: "16px",
        border: "1px dashed #c4c4c4",
        borderRadius: "8px",
        backgroundColor: "#fafafa",
        position: "relative",
      }}
    >
      <BlockStack gap="300">
        {previewUrl && (
          <div style={{ textAlign: "center" }}>
            <img
              src={previewUrl}
              alt={t("addMediaModal.imagePreviewAlt")}
              style={{
                maxWidth: "200px",
                maxHeight: "150px",
                objectFit: "contain",
                borderRadius: "4px",
                border: "1px solid #e1e1e1",
              }}
            />
          </div>
        )}
        {uploadedFile && (
          <Text as="p" variant="bodyMd" alignment="center">
            {uploadedFile.name}
          </Text>
        )}
      </BlockStack>

      <div
        style={{ position: "absolute", top: "8px", right: "8px" }}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          handleRemoveFile();
        }}
      >
        <Button
          icon={XIcon}
          variant="plain"
          tone="critical"
          accessibilityLabel={t("addMediaModal.removeUploadedFileLabel")}
        />
      </div>
    </div>
  );

  // Determine button text based on mode and type
  const getPrimaryActionText = () => {
    if (editingItem) {
      return mediaType === "vid"
        ? t("addMediaModal.updateVideo")
        : t("addMediaModal.updateImage");
    }
    if (mediaType === "vid") {
      return t("addMediaModal.addVideo");
    }
    return pendingImages.length > 1
      ? t("addMediaModal.addImages")
      : t("addMediaModal.addImage");
  };

  const isLoading = uploadProgress !== null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        editingItem ? t("addMediaModal.editTitle") : t("addMediaModal.title")
      }
      primaryAction={{
        content: getPrimaryActionText(),
        onAction: handleSubmit,
        loading: isLoading,
        disabled: isLoading,
      }}
      secondaryActions={[
        {
          content: t("addMediaModal.cancel"),
          onAction: handleClose,
          disabled: isLoading,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      {/* Loading Overlay */}
      {uploadProgress && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            gap: "16px",
          }}
        >
          <Spinner size="large" />
          <Text as="p" variant="bodyLg" fontWeight="semibold">
            {t("addMediaModal.uploading", {
              current: uploadProgress.current,
              total: uploadProgress.total,
            })}
          </Text>
        </div>
      )}

      <Modal.Section>
        <BlockStack gap="400">
          {/* Media Type Selection - Hide in edit mode */}
          {!editingItem && (
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                {t("addMediaModal.mediaTypeQuestion")}
              </Text>
              <InlineStack gap="400">
                <RadioButton
                  label={t("addMediaModal.video")}
                  checked={mediaType === "vid"}
                  id="video"
                  name="mediaType"
                  onChange={() => setMediaType("vid")}
                  disabled={isLoading}
                />
                <RadioButton
                  label={t("addMediaModal.image")}
                  checked={mediaType === "img"}
                  id="image"
                  name="mediaType"
                  onChange={() => setMediaType("img")}
                  disabled={isLoading}
                />
              </InlineStack>
            </BlockStack>
          )}

          {mediaType === "img" && !editingItem && (
            <Checkbox
              label={t("addMediaModal.automaticImageDescription")}
              checked={automaticallyGenerateDescription}
              onChange={handleAutomaticGenerationChange}
            />
          )}

          {/* Video Form */}
          {mediaType === "vid" && (
            <VideoForm
              mediaName={mediaName}
              youtubeUrl={youtubeUrl as string}
              errors={errors}
              onNameChange={(value) => {
                setMediaName(value);
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              onUrlChange={(value) => {
                setYoutubeUrl(value);
                setErrors((prev) => ({ ...prev, youtubeUrl: "" }));
              }}
            />
          )}

          {/* Images Form - Multi-upload (only in add mode) */}
          {mediaType === "img" && !editingItem && (
            <ImagesForm
              pendingImages={pendingImages}
              onAddFiles={handleAddFiles}
              onRemoveImage={handleRemoveImage}
              onUpdateImageName={handleUpdateImageName}
              onCancelDescriptionLoading={handleCancelDescriptionLoading}
            />
          )}

          {/* Images Form - Single edit mode */}
          {mediaType === "img" && editingItem && (
            <BlockStack gap="300">
              <BlockStack gap="200">
                <GenerativeTextField
                  id="image-title-bg"
                  placementId="event.event.image.title"
                  label={t("addMediaModal.imageTitle")}
                  value={mediaName}
                  onChange={(value) => {
                    setMediaName(value);
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  type="textField"
                  canGenerate={true}
                  requiredIndicator
                  error={errors.name}
                />
              </BlockStack>

              <BlockStack gap="200">
                <DropZone
                  onDrop={handleFileUpload}
                  accept="image/png,image/jpg, image/jpeg, image/gif, image/webp, image/avif, image/heic, image/tiff, image/svg"
                >
                  {uploadedFile || previewUrl ? (
                    fileUpload
                  ) : (
                    <DropZone.FileUpload
                      actionTitle={t("addMediaModal.addImageOrPhoto")}
                    />
                  )}
                </DropZone>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t("addMediaModal.imageResizeNote")}
                </Text>
              </BlockStack>
            </BlockStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
